import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Grid,
  Button,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Tv as TvIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import {
  getCustomerDisplaySettings,
  saveCustomerDisplaySettings,
  addCustomerDisplayImage,
  deleteCustomerDisplayImage,
  reorderCustomerDisplayImages,
  CustomerDisplayImageDto,
  SaveCustomerDisplaySettingsDto,
} from '../../../../services/salesService';
// ✅ Resim Kütüphanesi Modal'ı import
import ImageLibrarySelectionModal from '../../../accounting/inventory/ImageLibrarySelectionModal';
import { useTranslation } from 'react-i18next';

// ════════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════════

export interface CustomerDisplaySettings {
  settingsId: number | null;
  isEnabled: boolean;
  animationType: string;
  slideInterval: number;
  showCartOnActivity: boolean;
  cartDisplayDuration: number;
  cartBackgroundColor: string;
  cartTextColor: string;
  cartTotalFontSize: number;
  cartSideImageUrl: string | null; // ✅ YENİ: Sepet yan panel resmi
  images: CustomerDisplayImageDto[];
}

interface CustomerDisplaySettingsTabProps {
  screenId: number;
  companyId: string;
  // Controlled props - parent'tan gelen değerler
  settings: CustomerDisplaySettings;
  initialSettings: CustomerDisplaySettings | null;
  onSettingsChange: (settings: CustomerDisplaySettings) => void;
  // Save trigger from parent
  saving?: boolean;
}

interface AnimationOption {
  value: string;
  label: string;
  description: string;
}

// ════════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════════════════════

const ANIMATION_OPTIONS: AnimationOption[] = [
  { value: 'fade', label: 'fade', description: 'fade' },
  { value: 'slide', label: 'slide', description: 'slide' },
  { value: 'zoom', label: 'zoom', description: 'zoom' },
  { value: 'flip', label: 'flip', description: 'flip' },
  { value: 'none', label: 'none', description: 'none' },
];

// Default settings
export const DEFAULT_CUSTOMER_DISPLAY_SETTINGS: CustomerDisplaySettings = {
  settingsId: null,
  isEnabled: false,
  animationType: 'fade',
  slideInterval: 5,
  showCartOnActivity: true,
  cartDisplayDuration: 10,
  cartBackgroundColor: '#ffffff',
  cartTextColor: '#000000',
  cartTotalFontSize: 48,
  cartSideImageUrl: null, // ✅ YENİ: Sepet yan panel resmi
  images: [],
};

// ════════════════════════════════════════════════════════════════════════════════
// HELPER: Load settings from API
// ════════════════════════════════════════════════════════════════════════════════

export async function loadCustomerDisplaySettings(
  screenId: number
): Promise<CustomerDisplaySettings> {
  try {
    console.log('📺 [CustomerDisplay] Ayarlar yükleniyor...', { screenId });
    const response = await getCustomerDisplaySettings(screenId);
    
    if (response.Success && response.Data) {
      const data = response.Data;
      return {
        settingsId: data.Id,
        isEnabled: data.IsEnabled,
        animationType: data.AnimationType || 'fade',
        slideInterval: data.SlideInterval || 5,
        showCartOnActivity: data.ShowCartOnActivity,
        cartDisplayDuration: data.CartDisplayDuration || 10,
        cartBackgroundColor: data.CartBackgroundColor || '#ffffff',
        cartTextColor: data.CartTextColor || '#000000',
        cartTotalFontSize: data.CartTotalFontSize || 48,
        cartSideImageUrl: data.CartSideImageUrl || null, // ✅ YENİ
        images: data.Images || [],
      };
    }
    return { ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS };
  } catch (err) {
    console.error('❌ [CustomerDisplay] Ayarlar yüklenirken hata:', err);
    return { ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS };
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// HELPER: Save settings to API
// ════════════════════════════════════════════════════════════════════════════════

export async function saveCustomerDisplaySettingsToApi(
  screenId: number,
  settings: CustomerDisplaySettings
): Promise<{ success: boolean; settingsId: number | null; message?: string }> {
  try {
    const dto: SaveCustomerDisplaySettingsDto = {
      QuickSaleScreenId: screenId,
      IsEnabled: settings.isEnabled,
      AnimationType: settings.animationType,
      SlideInterval: settings.slideInterval,
      ShowCartOnActivity: settings.showCartOnActivity,
      CartDisplayDuration: settings.cartDisplayDuration,
      CartBackgroundColor: settings.cartBackgroundColor,
      CartTextColor: settings.cartTextColor,
      CartTotalFontSize: settings.cartTotalFontSize,
      CartSideImageUrl: settings.cartSideImageUrl, // ✅ YENİ
    };
    
    console.log('💾 [CustomerDisplay] Ayarlar kaydediliyor...', dto);
    const response = await saveCustomerDisplaySettings(dto);
    
    if (response.Success && response.Data) {
      return { success: true, settingsId: response.Data.Id };
    }
    return { success: false, settingsId: null, message: response.Message };
  } catch (err: any) {
    console.error('❌ [CustomerDisplay] Kaydetme hatası:', err);
    return { success: false, settingsId: null, message: err.message };
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

const CustomerDisplaySettingsTab: React.FC<CustomerDisplaySettingsTabProps> = ({
  screenId,
  companyId,
  settings,
  initialSettings,
  onSettingsChange,
  saving = false,
}) => {
  const { t } = useTranslation('salesSystem');
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL STATE (for operations that don't need parent sync)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [addingImage, setAddingImage] = useState(false);
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [savingForImages, setSavingForImages] = useState(false);
  const [showCartSideImageLibrary, setShowCartSideImageLibrary] = useState(false); // ✅ YENİ: Sepet yan panel resmi seçimi

  // ─────────────────────────────────────────────────────────────────────────────
  // SETTINGS CHANGE HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const updateSettings = (updates: Partial<CustomerDisplaySettings>) => {
    onSettingsChange({ ...settings, ...updates });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // IMAGE OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Resim kütüphanesi modalını aç (gerekirse önce ayarları kaydet)
  const handleOpenImageLibrary = async () => {
    // Eğer settingsId yoksa önce ayarları kaydet
    if (!settings.settingsId) {
      setSavingForImages(true);
      setError(null);
      
      try {
        const result = await saveCustomerDisplaySettingsToApi(screenId, settings);
        
        if (result.success && result.settingsId) {
          updateSettings({ settingsId: result.settingsId });
          console.log('✅ [CustomerDisplay] Ayarlar kaydedildi, şimdi resim kütüphanesi açılıyor');
          setImageLibraryOpen(true);
        } else {
          setError(result.message || t('customerDisplaySettingsTab.errors.settingsNotSaved'));
        }
      } catch (err: any) {
        console.error('❌ [CustomerDisplay] Kaydetme hatası:', err);
        setError(t('customerDisplaySettingsTab.errors.settingsSaveFailed'));
      } finally {
        setSavingForImages(false);
      }
    } else {
      setImageLibraryOpen(true);
    }
  };

  // Resim kütüphanesinden seçilen resimleri ekle
  const handleImageLibrarySave = async (selectedUrls: string[]) => {
    if (!settings.settingsId) {
      setError(t('customerDisplaySettingsTab.errors.settingsNotSavedTryAgain'));
      return;
    }
    
    setAddingImage(true);
    setError(null);
    
    try {
      const existingUrls = settings.images.map(img => img.ImageUrl);
      const newUrls = selectedUrls.filter(url => !existingUrls.includes(url));
      
      if (newUrls.length === 0) {
        setSuccessMessage(t('customerDisplaySettingsTab.messages.imagesAlreadyAdded'));
        setTimeout(() => setSuccessMessage(null), 3000);
        return;
      }
      
      const newImages: CustomerDisplayImageDto[] = [];
      for (let i = 0; i < newUrls.length; i++) {
        const url = newUrls[i];
        const response = await addCustomerDisplayImage({
          CustomerDisplaySettingsId: settings.settingsId,
          ImageUrl: url,
          ImageTitle: `Resim ${settings.images.length + i + 1}`,
          DisplayOrder: settings.images.length + i,
        });
        
        if (response.Success && response.Data) {
          newImages.push(response.Data);
        }
      }
      
      if (newImages.length > 0) {
        updateSettings({ images: [...settings.images, ...newImages] });
        setSuccessMessage(`${newImages.length} resim eklendi!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      setError(t('customerDisplaySettingsTab.errors.imageAddFailed'));
    } finally {
      setAddingImage(false);
      setImageLibraryOpen(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm(t('customerDisplaySettingsTab.confirm.deleteImage'))) return;
    
    try {
      const response = await deleteCustomerDisplayImage(imageId);
      
      if (response.Success) {
        updateSettings({ images: settings.images.filter(img => img.Id !== imageId) });
        setSuccessMessage(t('customerDisplaySettingsTab.messages.imageDeleted'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.Message || t('customerDisplaySettingsTab.errors.imageDeleteFailed'));
      }
    } catch (err: any) {
      setError(t('customerDisplaySettingsTab.errors.imageDeleteFailed'));
    }
  };

  const handleMoveImage = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= settings.images.length) return;
    
    const newImages = [...settings.images];
    const temp = newImages[index];
    newImages[index] = newImages[newIndex];
    newImages[newIndex] = temp;
    
    const updatedImages = newImages.map((img, idx) => ({ ...img, DisplayOrder: idx }));
    updateSettings({ images: updatedImages });
    
    try {
      await reorderCustomerDisplayImages(
        updatedImages.map(img => ({ Id: img.Id, DisplayOrder: img.DisplayOrder }))
      );
    } catch (err) {
      console.error('Sıralama güncellenirken hata:', err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // OPEN CUSTOMER DISPLAY WINDOW
  // ─────────────────────────────────────────────────────────────────────────────
  
  const handleOpenCustomerDisplay = () => {
    const url = `/#/customer-display/${screenId}?companyId=${encodeURIComponent(companyId)}`;
    const windowFeatures = 'width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no';
    window.open(url, 'CustomerDisplay', windowFeatures);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TvIcon sx={{ fontSize: 32, color: settings.isEnabled ? 'primary.main' : 'text.disabled' }} />
        <Box>
          <Typography variant="h6">{t('quickSaleSettingsModal.tabs.customerDisplay')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('customerDisplaySettingsTab.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sol Kolon: Genel Ayarlar */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('customerDisplaySettingsTab.generalSettings')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Aktif/Pasif Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.isEnabled}
                  onChange={(e) => updateSettings({ isEnabled: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    {settings.isEnabled ? t('customerDisplaySettingsTab.active') : t('customerDisplaySettingsTab.passive')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('customerDisplaySettingsTab.activeDescription')}
                  </Typography>
                </Box>
              }
              sx={{ mb: 3, ml: 0 }}
            />

            {/* Animasyon Tipi */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('customerDisplaySettingsTab.animationType')}</InputLabel>
              <Select
                value={settings.animationType}
                onChange={(e) => updateSettings({ animationType: e.target.value })}
                label={t('customerDisplaySettingsTab.animationType')}
                disabled={!settings.isEnabled}
              >
                {ANIMATION_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography>{t(`customerDisplaySettingsTab.animationOptions.${option.value}.label`)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`customerDisplaySettingsTab.animationOptions.${option.value}.description`)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Geçiş Süresi */}
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                {t('customerDisplaySettingsTab.imageTransitionDuration', { count: settings.slideInterval })}
              </Typography>
              <Slider
                value={settings.slideInterval}
                onChange={(_, value) => updateSettings({ slideInterval: value as number })}
                min={2}
                max={30}
                step={1}
                marks={[
                  { value: 2, label: '2s' },
                  { value: 10, label: '10s' },
                  { value: 20, label: '20s' },
                  { value: 30, label: '30s' },
                ]}
                disabled={!settings.isEnabled}
              />
            </Box>

            {/* Müşteri Ekranını Aç Butonu */}
            {settings.isEnabled && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenCustomerDisplay}
                fullWidth
                sx={{ mt: 2 }}
              >
                {t('customerDisplaySettingsTab.openCustomerDisplay')}
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Sağ Kolon: Sepet Ayarları */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('customerDisplaySettingsTab.cartViewSettings')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Sepet Aktivitesinde Göster */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showCartOnActivity}
                  onChange={(e) => updateSettings({ showCartOnActivity: e.target.checked })}
                  color="primary"
                  disabled={!settings.isEnabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    {t('customerDisplaySettingsTab.showOnCartActivity')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('customerDisplaySettingsTab.showOnCartActivityDescription')}
                  </Typography>
                </Box>
              }
              sx={{ mb: 3, ml: 0 }}
            />

            {/* Sepet Görüntüleme Süresi */}
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                {t('customerDisplaySettingsTab.returnToAdsDuration', { count: settings.cartDisplayDuration })}
              </Typography>
              <Slider
                value={settings.cartDisplayDuration}
                onChange={(_, value) => updateSettings({ cartDisplayDuration: value as number })}
                min={5}
                max={60}
                step={5}
                marks={[
                  { value: 5, label: '5s' },
                  { value: 30, label: '30s' },
                  { value: 60, label: '60s' },
                ]}
                disabled={!settings.isEnabled || !settings.showCartOnActivity}
              />
              <Typography variant="caption" color="text.secondary">
                {t('customerDisplaySettingsTab.returnToAdsHint')}
              </Typography>
            </Box>

            {/* Renkler */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>{t('customerDisplaySettingsTab.backgroundColor')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={settings.cartBackgroundColor}
                    onChange={(e) => updateSettings({ cartBackgroundColor: e.target.value })}
                    disabled={!settings.isEnabled}
                    style={{ width: 50, height: 35, border: 'none', cursor: 'pointer' }}
                  />
                  <Typography variant="caption">{settings.cartBackgroundColor}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>{t('customerDisplaySettingsTab.textColor')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={settings.cartTextColor}
                    onChange={(e) => updateSettings({ cartTextColor: e.target.value })}
                    disabled={!settings.isEnabled}
                    style={{ width: 50, height: 35, border: 'none', cursor: 'pointer' }}
                  />
                  <Typography variant="caption">{settings.cartTextColor}</Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Toplam Font Boyutu */}
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                {t('customerDisplaySettingsTab.totalAmountFontSize', { value: settings.cartTotalFontSize })}
              </Typography>
              <Slider
                value={settings.cartTotalFontSize}
                onChange={(_, value) => updateSettings({ cartTotalFontSize: value as number })}
                min={24}
                max={96}
                step={4}
                marks={[
                  { value: 24, label: '24' },
                  { value: 48, label: '48' },
                  { value: 72, label: '72' },
                  { value: 96, label: '96' },
                ]}
                disabled={!settings.isEnabled}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ✅ YENİ: Sepet Yan Panel Resmi */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {t('customerDisplaySettingsTab.cartSideImage')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('customerDisplaySettingsTab.cartSideImageDescription')}
              </Typography>
              
              {settings.cartSideImageUrl ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50'
                }}>
                  <Box
                    component="img"
                    src={settings.cartSideImageUrl}
                    alt={t('customerDisplaySettingsTab.cartSideImage')}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {settings.cartSideImageUrl.split('/').pop()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('customerDisplaySettingsTab.selectedImage')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={() => setShowCartSideImageLibrary(true)}
                      disabled={!settings.isEnabled}
                    >
                      {t('customerDisplaySettingsTab.change')}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => updateSettings({ cartSideImageUrl: null })}
                      disabled={!settings.isEnabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<PhotoLibraryIcon />}
                  onClick={() => setShowCartSideImageLibrary(true)}
                  disabled={!settings.isEnabled}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {t('customerDisplaySettingsTab.selectFromLibrary')}
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Alt Bölüm: Resimler */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('customerDisplaySettingsTab.adImages', { count: settings.images.length })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('customerDisplaySettingsTab.adImagesDescription')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={(addingImage || savingForImages) ? <CircularProgress size={16} color="inherit" /> : <PhotoLibraryIcon />}
                onClick={handleOpenImageLibrary}
                disabled={!settings.isEnabled || addingImage || savingForImages || saving}
                size="small"
              >
                {savingForImages ? t('customerDisplaySettingsTab.saving') : addingImage ? t('customerDisplaySettingsTab.adding') : t('customerDisplaySettingsTab.selectFromLibrary')}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {!settings.isEnabled && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('customerDisplaySettingsTab.enableFirst')}
              </Alert>
            )}

            {settings.images.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6, 
                bgcolor: 'grey.100', 
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}>
                <ImageIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography color="text.secondary">
                  {t('customerDisplaySettingsTab.noImageYet')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('customerDisplaySettingsTab.noImageHint')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {settings.images.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={image.Id}>
                    <Card sx={{ position: 'relative' }}>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 1,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                        }}
                      />
                      <CardMedia
                        component="img"
                        height="150"
                        image={image.ImageUrl}
                        alt={image.ImageTitle || `Resim ${index + 1}`}
                        sx={{ objectFit: 'cover' }}
                        onError={(e: any) => {
                          e.target.src = 'https://via.placeholder.com/300x150?text=Resim+Yüklenemedi';
                        }}
                      />
                      <CardActions sx={{ justifyContent: 'space-between' }}>
                        <Box>
                          <Tooltip title={t('customerDisplaySettingsTab.moveUp')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveImage(index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('customerDisplaySettingsTab.moveDown')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveImage(index, 'down')}
                                disabled={index === settings.images.length - 1}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        <Tooltip title={t('customerDisplaySettingsTab.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteImage(image.Id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ✅ Resim Kütüphanesi Seçim Modal'ı - Reklam Resimleri */}
      <ImageLibrarySelectionModal
        open={imageLibraryOpen}
        onClose={() => setImageLibraryOpen(false)}
        onSave={handleImageLibrarySave}
        companyId={companyId}
        productName={t('customerDisplaySettingsTab.adImagesProductName')}
        currentImageUrls={settings.images.map(img => img.ImageUrl)}
        productId={settings.settingsId?.toString() || null}
      />

      {/* ✅ YENİ: Sepet Yan Panel Resmi Seçim Modal'ı */}
      <ImageLibrarySelectionModal
        open={showCartSideImageLibrary}
        onClose={() => setShowCartSideImageLibrary(false)}
        onSave={(selectedUrls) => {
          // Sadece ilk seçilen resmi al (tek seçim)
          if (selectedUrls.length > 0) {
            updateSettings({ cartSideImageUrl: selectedUrls[0] });
          }
          setShowCartSideImageLibrary(false);
        }}
        companyId={companyId}
        productName={t('customerDisplaySettingsTab.cartSideImageProductName')}
        currentImageUrls={settings.cartSideImageUrl ? [settings.cartSideImageUrl] : []}
        productId={settings.settingsId?.toString() || null}
        singleSelect={true}
      />
    </Box>
  );
};

export default CustomerDisplaySettingsTab;
